import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import KPICard from '../Dashboard/KPICard';

const { FiDollarSign, FiTrendingUp, FiCalendar, FiShield } = FiIcons;

const CustomerKPIs = ({ customerId }) => {
  const customerName = customerId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Mock KPI data based on customer
  const kpiData = [
    {
      title: 'Last 12 Months Purchases',
      value: '$28.5M',
      change: '+15.3%',
      changeType: 'positive',
      icon: FiDollarSign,
      description: 'vs Previous 12 Months',
      color: 'blue'
    },
    {
      title: 'Current FY Purchases',
      value: '$22.8M',
      change: '+12.7%',
      changeType: 'positive',
      icon: FiTrendingUp,
      description: 'April 2024 - March 2025',
      color: 'green'
    },
    {
      title: 'Current Quarter',
      value: '$7.2M',
      change: '+18.2%',
      changeType: 'positive',
      icon: FiCalendar,
      description: 'Q2 FY2025',
      color: 'purple'
    },
    {
      title: 'Current Month',
      value: '$2.4M',
      change: '+8.9%',
      changeType: 'positive',
      icon: FiDollarSign,
      description: 'vs Same Month Last Year',
      color: 'orange'
    }
  ];

  const serializationScore = 100; // Usually should be 100% for all customers

  return (
    <div className="space-y-6">
      {/* Main KPIs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {kpiData.map((kpi, index) => (
          <KPICard
            key={index}
            {...kpi}
            delay={index * 0.1}
          />
        ))}
      </motion.div>

      {/* Serialization Scorecard */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <SafeIcon icon={FiShield} className="text-green-600" />
          Serialization Scorecard
        </h3>
        
        <div className="flex items-center justify-between p-6 bg-green-50 rounded-xl border border-green-200">
          <div>
            <p className="text-sm text-green-700 mb-1">Latest Month Performance</p>
            <p className="text-3xl font-bold text-green-900">{serializationScore}%</p>
            <p className="text-sm text-green-700">Serialization Compliance</p>
          </div>
          <div className="text-right">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-2">
              <SafeIcon icon={FiShield} className="text-white text-2xl" />
            </div>
            <p className="text-xs text-green-700">Perfect Score</p>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Note:</strong> Serialization scorecard should maintain 100% compliance for all customers 
            to ensure regulatory compliance and supply chain integrity.
          </p>
        </div>
      </motion.div>

      {/* Customer Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        {/* Growth Trend */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">Growth Trend</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">YoY Growth Rate</span>
              <span className="font-medium text-green-600">+15.3%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Quarterly Growth</span>
              <span className="font-medium text-green-600">+18.2%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Monthly Growth</span>
              <span className="font-medium text-green-600">+8.9%</span>
            </div>
          </div>
        </div>

        {/* Customer Profile */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">Customer Profile</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Customer Type</span>
              <span className="font-medium text-gray-900">Wholesaler</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active Products</span>
              <span className="font-medium text-gray-900">23</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Relationship</span>
              <span className="font-medium text-gray-900">5+ Years</span>
            </div>
          </div>
        </div>

        {/* Performance Status */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">Performance Status</h4>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-700">Revenue Target: Exceeded</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-700">Volume Target: Exceeded</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-700">Compliance: Perfect</span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CustomerKPIs;